import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBreakdownVersions } from '@/hooks/useBreakdown';
import { jobKeys } from '@/hooks/useJobs';
import type { JobBreakdown } from '@jobos/shared';

export type VersionSnapshot = {
  id: string;
  name: string;
  createdAt: string;
  snapshot: JobBreakdown;
};

export interface UseBreakdownVersionPreviewOptions<TSlice> {
  jobId: string;
  activeBreakdownVersionId?: string | null;
  breakdownModifiedSinceVersion?: boolean;
  /**
   * Extract the view-specific slice to render from a version's snapshot.
   * Carpet returns just the carpet line item's sub-items; generic returns
   * the full flat-items shape. Omit if no slice is needed.
   */
  extractSlice?: (snapshot: JobBreakdown) => TSlice;
  /** View-specific side effects when a version is selected for preview. */
  onSelectVersion?: (version: VersionSnapshot) => void;
  /** View-specific side effects when exiting preview mode. */
  onExitPreview?: () => void;
  /**
   * View-specific cleanup after a successful restore (clearing undo stacks,
   * resetting load flags, etc.). Query invalidation is handled internally.
   */
  onAfterRestore?: () => void;
}

export interface UseBreakdownVersionPreviewReturn<TSlice> {
  previewingVersion: VersionSnapshot | null;
  isPreviewMode: boolean;
  restoreDialogOpen: boolean;
  setRestoreDialogOpen: (open: boolean) => void;
  versionLabel: string | undefined;
  editedSinceVersion: boolean;
  markEdited: () => void;
  /** Extracted slice of the previewed snapshot, or null when not previewing. */
  previewSlice: TSlice | null;
  handleSelectVersion: (version: VersionSnapshot) => void;
  handleExitPreview: () => void;
  handleRestoreSuccess: () => void;
  handleVersionSaved: () => void;
}

/**
 * Shared version-preview state for any breakdown-backed editor (generic or
 * specialty). Owns:
 *   - `previewingVersion` + `isPreviewMode` state,
 *   - The restore-confirm dialog open state,
 *   - The version label with position + " *" modified marker,
 *   - `editedSinceVersion` tracking (caller calls `markEdited()` on every save),
 *   - Extracted preview slice via the caller-provided `extractSlice`.
 *
 * View-specific behavior — selecting the first item, clearing an undo stack,
 * resetting a local load flag — is injected via the `onSelectVersion`,
 * `onExitPreview`, `onAfterRestore` callbacks.
 */
export function useBreakdownVersionPreview<TSlice>({
  jobId,
  activeBreakdownVersionId,
  breakdownModifiedSinceVersion,
  extractSlice,
  onSelectVersion,
  onExitPreview,
  onAfterRestore,
}: UseBreakdownVersionPreviewOptions<TSlice>): UseBreakdownVersionPreviewReturn<TSlice> {
  const queryClient = useQueryClient();
  const [previewingVersion, setPreviewingVersion] = useState<VersionSnapshot | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [editedSinceVersion, setEditedSinceVersion] = useState(false);

  useEffect(() => {
    setEditedSinceVersion(false);
  }, [breakdownModifiedSinceVersion, activeBreakdownVersionId]);

  const { data: versions } = useBreakdownVersions(jobId);
  const versionLabel = useMemo(() => {
    const versionId = previewingVersion?.id ?? activeBreakdownVersionId;
    if (!versionId || !versions?.length) return undefined;
    const index = versions.findIndex((v) => v.id === versionId);
    const version = versions[index];
    if (index === -1 || !version) return undefined;
    const name = version.name;
    const position = `(${index + 1}/${versions.length})`;
    const label = `${name} ${position}`;
    if (previewingVersion) return label;
    const isModified = editedSinceVersion || breakdownModifiedSinceVersion;
    return isModified ? `${label} *` : label;
  }, [
    previewingVersion,
    activeBreakdownVersionId,
    versions,
    editedSinceVersion,
    breakdownModifiedSinceVersion,
  ]);

  const markEdited = useCallback(() => {
    setEditedSinceVersion(true);
  }, []);

  const handleSelectVersion = useCallback(
    (version: VersionSnapshot) => {
      setPreviewingVersion(version);
      onSelectVersion?.(version);
    },
    [onSelectVersion],
  );

  const handleExitPreview = useCallback(() => {
    setPreviewingVersion(null);
    onExitPreview?.();
  }, [onExitPreview]);

  const handleRestoreSuccess = useCallback(() => {
    setPreviewingVersion(null);
    setEditedSinceVersion(false);
    onAfterRestore?.();
    queryClient.invalidateQueries({ queryKey: jobKeys.all });
  }, [onAfterRestore, queryClient]);

  const handleVersionSaved = useCallback(() => {
    setEditedSinceVersion(false);
    queryClient.invalidateQueries({ queryKey: jobKeys.all });
  }, [queryClient]);

  const previewSlice = useMemo<TSlice | null>(
    () =>
      previewingVersion && extractSlice
        ? extractSlice(previewingVersion.snapshot)
        : null,
    [previewingVersion, extractSlice],
  );

  return {
    previewingVersion,
    isPreviewMode: previewingVersion !== null,
    restoreDialogOpen,
    setRestoreDialogOpen,
    versionLabel,
    editedSinceVersion,
    markEdited,
    previewSlice,
    handleSelectVersion,
    handleExitPreview,
    handleRestoreSuccess,
    handleVersionSaved,
  };
}
